import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categoriesAPI } from '../services/api';

import imgPhonesTablets from '../assets/trending/phones-tablets.webp';
import imgLaptopsComputers from '../assets/trending/laptops-computers.webp';
import imgGamingHandheld from '../assets/trending/gaming-handheld.webp';
import imgGamingConsole from '../assets/trending/gaming-console.webp';
import imgAudioSpeaker from '../assets/trending/audio-speaker.webp';
import imgVideoCameras from '../assets/trending/video-cameras.webp';
import imgWearables from '../assets/trending/wearables.webp';

// Static visuals per category — cards show what the label says, not live listings.
// Categories with two images cycle them in a slow crossfade carousel.
const CATEGORY_VISUALS = {
  'phones-tablets': {
    images: [imgPhonesTablets],
    description: 'Latest flagship phones and premium tablets',
  },
  'laptops-computers': {
    images: [imgLaptopsComputers],
    description: 'Powerful laptops and desktop computers',
  },
  'gaming': {
    images: [imgGamingHandheld, imgGamingConsole],
    description: 'Consoles, handhelds and gaming gear',
  },
  'audio-video': {
    images: [imgAudioSpeaker, imgVideoCameras],
    description: 'Speakers, action cameras and creator gear',
  },
  'wearables': {
    images: [imgWearables],
    description: 'Smartwatches and fitness trackers',
  },
};

// Shown if the categories API is unreachable — mirrors the live categories
const FALLBACK_CATEGORIES = [
  { _id: 'phones-tablets', name: 'Phones & Tablets', slug: 'phones-tablets' },
  { _id: 'laptops-computers', name: 'Laptops & Computers', slug: 'laptops-computers' },
  { _id: 'gaming', name: 'Gaming', slug: 'gaming' },
  { _id: 'audio-video', name: 'Audio & Video', slug: 'audio-video' },
  { _id: 'wearables', name: 'Wearables', slug: 'wearables' },
];

const slugify = (value) =>
  (value || '')
    .toLowerCase()
    .replace(/&/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const getVisuals = (category) => {
  const slug = category.slug || slugify(category.name);
  return CATEGORY_VISUALS[slug] || CATEGORY_VISUALS[slugify(category.name)] || CATEGORY_VISUALS['phones-tablets'];
};

const TrendingSection = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await categoriesAPI.getAll();
        const list = response?.data?.categories || response?.data || [];
        setCategories(Array.isArray(list) ? list.slice(0, 8) : []);
      } catch (error) {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Slow crossfade for cards that have more than one image
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 5000);
    return () => clearInterval(interval);
  }, []);

  const handleExplore = (category) => {
    const categoryId = category._id || category.id;
    navigate(`/shop?category=${categoryId}`);
  };

  const handleExploreMore = () => {
    navigate('/shop');
  };

  const displayCategories = categories.length > 0 ? categories : FALLBACK_CATEGORIES;

  if (loading) {
    return (
      <section className="ab-trending-section">
        <div className="ab-container">
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#22C55E' }}></i>
            <p style={{ marginTop: '1rem', fontSize: '1.2rem', opacity: 0.7 }}>Loading hot deals...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="ab-trending-section">
      {/* Animated Background */}
      <div className="ab-bg-animation">
        <div className="ab-floating-orb ab-orb-1"></div>
        <div className="ab-floating-orb ab-orb-2"></div>
        <div className="ab-floating-orb ab-orb-3"></div>
      </div>

      <div className="ab-container">
        {/* Section Header */}
        <motion.div
          className="ab-section-header ab-reveal"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="ab-section-title">Discover Hot Collections</h2>
          <button
            onClick={handleExploreMore}
            className="ab-more-btn"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'inherit',
              fontSize: 'inherit',
              fontFamily: 'inherit'
            }}
          >
            <span>Explore More</span>
            <i className="fas fa-arrow-right"></i>
          </button>
        </motion.div>

        {/* Category Cards */}
        <div className="ab-products-grid">
          {displayCategories.map((category, index) => {
            const categoryId = category._id || category.id;
            const visuals = getVisuals(category);
            // Stagger multi-image cards so they don't all flip at once
            const imageIndex = (tick + index) % visuals.images.length;
            const displayImage = visuals.images[imageIndex];
            const displayName = category.name || 'Category';

            return (
              <motion.div
                key={categoryId}
                className="ab-product-card"
                onClick={() => handleExplore(category)}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{
                  duration: 0.6,
                  delay: index * 0.1,
                  type: 'spring',
                  stiffness: 100
                }}
                whileHover={{
                  y: -10,
                  transition: { duration: 0.3 }
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.img
                    key={`${categoryId}-${imageIndex}`}
                    src={displayImage}
                    alt={displayName}
                    className="ab-product-image"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                  />
                </AnimatePresence>

                {/* Mobile-visible bottom bar — always shown on small screens, hidden on desktop */}
                <div className="ab-mobile-info">
                  <div className="ab-mobile-info-top">
                    <h3 className="ab-mobile-info-name">{displayName}</h3>
                  </div>
                  <p className="ab-mobile-info-desc">{visuals.description}</p>
                </div>

                {/* Desktop hover overlay */}
                <div className="ab-product-overlay">
                  <div>
                    <h3 className="ab-product-name">{displayName}</h3>
                    <p className="ab-overlay-count">{visuals.description}</p>
                  </div>

                  <motion.button
                    className="ab-explore-btn"
                    onClick={(e) => { e.stopPropagation(); handleExplore(category); }}
                    whileHover={{ x: 5 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span>Explore</span>
                    <i className="fas fa-arrow-right"></i>
                  </motion.button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Become a Seller CTA */}
        <motion.div
          className="ab-seller-cta"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            marginTop: '4rem',
            textAlign: 'center',
            padding: '3rem 2rem',
            background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(22, 163, 74, 0.1))',
            borderRadius: '20px',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}
        >
          <h3 style={{ fontSize: '2rem', marginBottom: '1rem', fontFamily: "'Vast Shadow', serif" }} className="seller-cta-title">
            Want to sell your gadgets?
          </h3>
          <style>{`
            .seller-cta-title {
              color: var(--text-light);
            }
            body.dark-mode .seller-cta-title {
              color: white;
            }
          `}</style>
          <p style={{ fontSize: '1.15rem', marginBottom: '2rem', opacity: 0.8, fontFamily: "'Urbanist', sans-serif", fontWeight: '500' }}>
            Join thousands of sellers and reach millions of buyers on AutoboyExpress
          </p>
          <a
            href="/become-seller"
            style={{
              display: 'inline-block',
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, #22C55E, #16A34A)',
              color: 'white',
              borderRadius: '50px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '1.1rem',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(34, 197, 94, 0.3)'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 15px 40px rgba(34, 197, 94, 0.4)';
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 10px 30px rgba(34, 197, 94, 0.3)';
            }}
          >
            Become a Seller <i className="fas fa-arrow-right" style={{ marginLeft: '0.5rem' }}></i>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TrendingSection;
